import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, FileText, Loader2, Trash2, Upload, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const BASE = process.env.REACT_APP_BACKEND_URL + '/api/actualizacion-familiar';

export const ESTADOS_DOC = {
  pendiente_carga: { label: 'Pendiente de carga', cls: 'bg-amber-100 text-amber-800' },
  en_revision: { label: 'En revisión', cls: 'bg-blue-100 text-blue-800' },
  aprobada: { label: 'Aprobada', cls: 'bg-green-100 text-green-800' },
  rechazada: { label: 'Rechazada', cls: 'bg-red-100 text-red-800' },
};

export const EstadoDocBadge = ({ estado, testid }) => {
  if (!estado) return <span className="text-xs text-gray-400" data-testid={testid}>No solicitada</span>;
  const e = ESTADOS_DOC[estado] || { label: estado, cls: 'bg-gray-100' };
  return <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${e.cls}`} data-testid={testid}>{e.label}</span>;
};

export const abrirArchivo = async (url) => {
  const w = window.open('', '_blank');
  try {
    const r = await axios.get(url, { responseType: 'blob' });
    const obj = URL.createObjectURL(r.data);
    if (w) w.location.href = obj; else window.location.href = obj;
  } catch (e) {
    w?.close();
    throw e;
  }
};

export const borrarConAviso = async (fn, set) => {
  try { set(await fn()); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo eliminar'); }
};

export const ListaArchivos = ({ archivos, urlDe, puedeBorrar, onBorrar }) => (
  <ul className="space-y-2" data-testid="doc-lista-archivos">
    {archivos.map((a) => (
      <li key={a.id} className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm" data-testid={`doc-archivo-${a.id}`}>
        <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
        <button className="flex-1 truncate text-left text-primary hover:underline" onClick={() => abrirArchivo(urlDe(a)).catch(() => toast.error('No se pudo abrir'))} data-testid={`doc-abrir-${a.id}`}>{a.nombre}</button>
        <span className="hidden sm:inline text-xs text-gray-400">{a.subido_por === 'matriculado' ? 'Subido por le matriculade' : `Subido por ${a.subido_por}`}</span>
        {puedeBorrar(a) && (
          <button onClick={() => onBorrar(a)} className="text-gray-400 hover:text-red-600" data-testid={`doc-borrar-${a.id}`}><Trash2 className="h-4 w-4" /></button>
        )}
      </li>
    ))}
    {!archivos.length && <li className="text-sm text-gray-400">Sin archivos cargados.</li>}
  </ul>
);

export const SubirArchivos = ({ onFiles, subiendo, label = 'Subir archivos', testid = 'doc-subir' }) => {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" multiple accept="application/pdf,image/*" className="hidden" data-testid={`${testid}-input`}
        onChange={(e) => { if (e.target.files.length) onFiles([...e.target.files]); e.target.value = ''; }} />
      <Button variant="outline" size="sm" disabled={subiendo} onClick={() => ref.current.click()} data-testid={`${testid}-btn`}>
        {subiendo ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{label}
      </Button>
    </>
  );
};

export const subirArchivos = async (url, files) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return (await axios.post(url, fd, { timeout: 120000 })).data.documentacion;
};

const Revision = ({ trabajadorId, onDone }) => {
  const [rechazando, setRechazando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const enviar = async (aprobar) => {
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/admin/documentacion/${trabajadorId}/revisar`, { aprobar, motivo });
      toast.success(aprobar ? 'Documentación aprobada' : r.data.notificado ? 'Rechazada y notificada por email' : 'Rechazada (no se pudo enviar el email)');
      setRechazando(false); setMotivo('');
      onDone(r.data.documentacion);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  if (rechazando) return (
    <div className="space-y-2" data-testid="doc-rechazo-form">
      <Textarea placeholder="Motivo del rechazo (se envía por email)" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="doc-motivo-input" />
      <div className="flex gap-2">
        <Button size="sm" variant="destructive" disabled={busy || !motivo.trim()} onClick={() => enviar(false)} data-testid="doc-confirmar-rechazo-btn">Rechazar y avisar</Button>
        <Button size="sm" variant="ghost" onClick={() => setRechazando(false)}>Cancelar</Button>
      </div>
    </div>
  );
  return (
    <div className="flex gap-2">
      <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={busy} onClick={() => enviar(true)} data-testid="doc-aprobar-btn"><Check className="h-4 w-4 mr-1" />Aprobar</Button>
      <Button size="sm" variant="outline" className="text-red-600" disabled={busy} onClick={() => setRechazando(true)} data-testid="doc-rechazar-btn"><X className="h-4 w-4 mr-1" />Rechazar</Button>
    </div>
  );
};

export const DocumentacionAdmin = ({ trabajadorId, onChange }) => {
  const [doc, setDoc] = useState(undefined);
  const [subiendo, setSubiendo] = useState(false);
  const actualizar = useCallback((d) => { setDoc(d); onChange?.(d); }, [onChange]);

  useEffect(() => {
    axios.get(`${BASE}/admin/documentacion/${trabajadorId}`).then((r) => setDoc(r.data.documentacion)).catch(() => setDoc(null));
  }, [trabajadorId]);

  const subir = async (files) => {
    setSubiendo(true);
    try { actualizar(await subirArchivos(`${BASE}/admin/documentacion/${trabajadorId}/archivos`, files)); toast.success('Archivo(s) anexado(s)'); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo subir'); } finally { setSubiendo(false); }
  };
  const borrar = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.nombre}"?`)) return;
    await borrarConAviso(async () => (await axios.delete(`${BASE}/admin/documentacion/${trabajadorId}/archivos/${a.id}`)).data.documentacion, actualizar);
  };

  if (doc === undefined) return <p className="text-sm text-gray-400">Cargando…</p>;
  const archivos = doc?.archivos || [];
  return (
    <div className="space-y-3" data-testid="doc-admin">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-500">Documentación:</span> <EstadoDocBadge estado={doc?.estado} testid="doc-admin-estado" />
        {doc?.revisado_por && <span className="text-xs text-gray-400">· revisada por {doc.revisado_por}</span>}
      </div>
      {doc?.estado === 'rechazada' && doc.motivo_rechazo && <p className="text-xs text-red-700">Motivo: {doc.motivo_rechazo}</p>}
      <ListaArchivos archivos={archivos} urlDe={(a) => `${BASE}/admin/documentacion/${trabajadorId}/archivos/${a.id}`} puedeBorrar={() => true} onBorrar={borrar} />
      <div className="flex flex-wrap gap-2">
        <SubirArchivos onFiles={subir} subiendo={subiendo} label="Anexar archivo" testid="doc-admin-subir" />
        {archivos.length > 0 && doc?.estado !== 'aprobada' && <Revision trabajadorId={trabajadorId} onDone={actualizar} />}
      </div>
    </div>
  );
};
